import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userPagination, setUserPagination] = useState({ page: 1, pages: 1 });
  const [settings, setSettings] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('');
  
  // Broadcast form
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastContent, setBroadcastContent] = useState('');
  const [broadcastTarget, setBroadcastTarget] = useState('');

  const token = localStorage.getItem('addison_token');

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchSettings();
    fetchAuditLog();
    fetchAnnouncements();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      setError('Failed to load stats');
    }
  };

  const fetchUsers = async (page = 1, search = '', tier = '') => {
    setLoading(true);
    try {
      let url = `/api/admin/users?page=${page}&limit=50`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (tier) url += `&tier=${tier}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
      setUserPagination(response.data.pagination || { page: 1, pages: 1 });
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
      setActiveTab('user-detail');
    } catch (err) {
      setError('Failed to load user details');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/admin/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data.settings || []);
    } catch (err) {
      console.error('Failed to load settings');
    }
  };

  const fetchAuditLog = async () => {
    try {
      const response = await axios.get('/api/admin/audit-log', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data.logs || []);
    } catch (err) {
      console.error('Failed to load audit log');
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get('/api/admin/announcements', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data.announcements || []);
    } catch (err) {
      console.error('Failed to load announcements');
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(`/api/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('User updated successfully');
      fetchUsers();
      if (selectedUser?.user?.id === userId) {
        fetchUserDetails(userId);
      }
    } catch (err) {
      setError('Failed to update user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('User deleted successfully');
      fetchUsers();
      setActiveTab('users');
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const updateSetting = async (key, value) => {
    try {
      await axios.put('/api/admin/settings', { key, value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Setting updated');
      fetchSettings();
    } catch (err) {
      setError('Failed to update setting');
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastTitle || !broadcastContent) {
      setError('Title and content are required');
      return;
    }
    try {
      await axios.post('/api/admin/broadcast', {
        title: broadcastTitle,
        content: broadcastContent,
        target_tier: broadcastTarget
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Broadcast sent successfully');
      setBroadcastTitle('');
      setBroadcastContent('');
      setBroadcastTarget('');
      fetchAnnouncements();
    } catch (err) {
      setError('Failed to send broadcast');
    }
  };

  const toggleAdmin = async (userId, isAdmin) => {
    try {
      await axios.put(`/api/admin/users/${userId}`, { is_admin: !isAdmin }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`User ${!isAdmin ? 'promoted to' : 'demoted from'} admin`);
      fetchUsers();
    } catch (err) {
      setError('Failed to update admin status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const renderDashboard = () => (
    <div>
      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>Admin Dashboard</h2>
      
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#DC2626' }}>{stats.users?.total_users || 0}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Total Users</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#10B981' }}>{stats.users?.new_this_week || 0}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>New This Week</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#3B82F6' }}>{stats.emails?.total_emails || 0}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Total Emails</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#F59E0B' }}>{stats.emails?.emails_24h || 0}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Emails (24h)</div>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>User Distribution</h3>
        {stats?.users && (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--darker)', padding: '12px 20px', borderRadius: '8px' }}>
              <strong>Free:</strong> {stats.users.free_users || 0}
            </div>
            <div style={{ background: 'var(--darker)', padding: '12px 20px', borderRadius: '8px' }}>
              <strong>Pro:</strong> {stats.users.pro_users || 0}
            </div>
            <div style={{ background: 'var(--darker)', padding: '12px 20px', borderRadius: '8px' }}>
              <strong>Enterprise:</strong> {stats.users.enterprise_users || 0}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderUsers = () => (
    <div>
      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>User Management</h2>
      
      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #EF4444', color: '#EF4444', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}
      {success && <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10B981', color: '#10B981', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{success}</div>}

      <div className="card" style={{ padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '10px 16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)' }}
          />
          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value)}
            style={{ padding: '10px 16px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)' }}
          >
            <option value="">All Tiers</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <button className="btn btn-primary" onClick={() => fetchUsers(1, searchQuery, filterTier)}>
            🔍 Search
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '12px' }}>Username</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Tier</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Admin</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Created</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px' }}>{user.username}</td>
                  <td style={{ padding: '12px', fontSize: '14px', color: 'var(--text-muted)' }}>{user.email || 'N/A'}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ 
                      padding: '4px 12px', 
                      borderRadius: '12px', 
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      background: user.subscription_tier === 'enterprise' ? '#8B5CF6' : user.subscription_tier === 'pro' ? '#3B82F6' : '#6B7280'
                    }}>{user.subscription_tier}</span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {user.is_admin ? '👑 Yes' : 'No'}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{formatDate(user.created_at)}</td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-ghost" style={{ padding: '6px 12px' }} onClick={() => fetchUserDetails(user.id)}>
                        👁️ View
                      </button>
                      <button className="btn btn-ghost" style={{ padding: '6px 12px' }} onClick={() => toggleAdmin(user.id, user.is_admin)}>
                        {user.is_admin ? '⬇️ Demote' : '⬆️ Promote'}
                      </button>
                      <button className="btn btn-ghost" style={{ padding: '6px 12px', color: '#EF4444' }} onClick={() => deleteUser(user.id)}>
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {userPagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          {Array.from({ length: userPagination.pages }, (_, i) => (
            <button
              key={i + 1}
              className={`btn ${userPagination.page === i + 1 ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => fetchUsers(i + 1, searchQuery, filterTier)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderUserDetail = () => {
    if (!selectedUser) return null;
    const { user, email_accounts, pgp_keys } = selectedUser;
    
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700' }}>User Details: {user.username}</h2>
          <button className="btn btn-ghost" onClick={() => setActiveTab('users')}>← Back to Users</button>
        </div>

        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #EF4444', color: '#EF4444', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}
        {success && <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10B981', color: '#10B981', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{success}</div>}

        <div className="card" style={{ padding: '24px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>User Info</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div><strong>ID:</strong> {user.id}</div>
            <div><strong>Username:</strong> {user.username}</div>
            <div><strong>Email:</strong> {user.email || 'N/A'}</div>
            <div><strong>Tier:</strong> {user.subscription_tier}</div>
            <div><strong>Admin:</strong> {user.is_admin ? 'Yes' : 'No'}</div>
            <div><strong>Created:</strong> {formatDate(user.created_at)}</div>
            <div><strong>Last Login:</strong> {formatDate(user.last_login)}</div>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={() => toggleAdmin(user.id, user.is_admin)}>
              {user.is_admin ? '⬇️ Remove Admin' : '⬆️ Make Admin'}
            </button>
            <button className="btn btn-secondary" onClick={() => updateUser(user.id, { subscription_tier: user.subscription_tier === 'free' ? 'pro' : 'free' })}>
              🔄 Toggle Tier
            </button>
            <button className="btn btn-ghost" style={{ color: '#EF4444' }} onClick={() => deleteUser(user.id)}>
              🗑️ Delete User
            </button>
          </div>
        </div>

        <div className="card" style={{ padding: '24px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Email Accounts ({email_accounts?.length || 0})</h3>
          {email_accounts?.map(acc => (
            <div key={acc.id} style={{ padding: '12px', background: 'var(--darker)', borderRadius: '8px', marginBottom: '8px' }}>
              {acc.email_address} {acc.is_active ? '✅' : '❌'}
            </div>
          ))}
        </div>

        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>PGP Keys ({pgp_keys?.length || 0})</h3>
          {pgp_keys?.map(key => (
            <div key={key.id} style={{ padding: '12px', background: 'var(--darker)', borderRadius: '8px', marginBottom: '8px', fontSize: '12px' }}>
              <div><strong>ID:</strong> {key.key_id}</div>
              <div><strong>Email:</strong> {key.email_address}</div>
              <div><strong>Created:</strong> {formatDate(key.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSettings = () => (
    <div>
      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>System Settings</h2>
      
      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #EF4444', color: '#EF4444', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}
      {success && <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10B981', color: '#10B981', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{success}</div>}

      <div className="card" style={{ padding: '24px' }}>
        {settings.map(setting => (
          <div key={setting.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{setting.key.replace(/_/g, ' ')}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{setting.description}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                Last updated: {formatDate(setting.updated_at)} by {setting.updated_by || 'system'}
              </div>
            </div>
            <div>
              {setting.value === 'true' || setting.value === 'false' ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={setting.value === 'true'}
                    onChange={(e) => updateSetting(setting.key, e.target.checked ? 'true' : 'false')}
                  />
                  <span>{setting.value === 'true' ? 'Enabled' : 'Disabled'}</span>
                </label>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={setting.value}
                    onChange={(e) => updateSetting(setting.key, e.target.value)}
                    style={{ padding: '8px 12px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', width: '150px' }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderBroadcast = () => (
    <div>
      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>Broadcast Message</h2>
      
      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #EF4444', color: '#EF4444', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}
      {success && <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10B981', color: '#10B981', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{success}</div>}

      <div className="card" style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Target Tier</label>
          <select
            value={broadcastTarget}
            onChange={(e) => setBroadcastTarget(e.target.value)}
            style={{ width: '100%', padding: '12px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)' }}
          >
            <option value="">All Users</option>
            <option value="free">Free Users Only</option>
            <option value="pro">Pro Users Only</option>
            <option value="enterprise">Enterprise Users Only</option>
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Title</label>
          <input
            type="text"
            value={broadcastTitle}
            onChange={(e) => setBroadcastTitle(e.target.value)}
            placeholder="Important Announcement"
            style={{ width: '100%', padding: '12px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)' }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Content</label>
          <textarea
            value={broadcastContent}
            onChange={(e) => setBroadcastContent(e.target.value)}
            rows={6}
            placeholder="Enter your message here..."
            style={{ width: '100%', padding: '12px', background: 'var(--darker)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', resize: 'vertical' }}
          />
        </div>

        <button className="btn btn-primary" style={{ width: '100%', padding: '16px' }} onClick={sendBroadcast}>
          📢 Send Broadcast
        </button>
      </div>

      <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>Past Announcements</h3>
      {announcements.map(ann => (
        <div key={ann.id} className="card" style={{ padding: '16px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <strong>{ann.title}</strong>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{formatDate(ann.created_at)}</span>
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>{ann.content}</div>
          <div style={{ fontSize: '12px' }}>
            <span style={{ background: 'var(--darker)', padding: '4px 8px', borderRadius: '4px', marginRight: '8px' }}>
              Target: {ann.target_tier || 'All'}
            </span>
            <span style={{ background: 'var(--darker)', padding: '4px 8px', borderRadius: '4px' }}>
              Recipients: {ann.sent_count}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderAuditLog = () => (
    <div>
      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>Admin Audit Log</h2>
      
      <div className="card" style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '12px' }}>Time</th>
              <th style={{ textAlign: 'left', padding: '12px' }}>Admin</th>
              <th style={{ textAlign: 'left', padding: '12px' }}>Action</th>
              <th style={{ textAlign: 'left', padding: '12px' }}>Target</th>
              <th style={{ textAlign: 'left', padding: '12px' }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {auditLogs.map(log => (
              <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '12px', fontSize: '12px' }}>{formatDate(log.created_at)}</td>
                <td style={{ padding: '12px' }}>{log.admin_username}</td>
                <td style={{ padding: '12px' }}>
                  <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    background: log.action.includes('DELETE') ? '#EF4444' : log.action.includes('UPDATE') ? '#F59E0B' : '#10B981'
                  }}>{log.action}</span>
                </td>
                <td style={{ padding: '12px', fontSize: '12px' }}>{log.target_type}:{log.target_id?.slice(0, 8)}...</td>
                <td style={{ padding: '12px', fontSize: '12px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {log.details}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="fade-in" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Sidebar */}
        <div style={{ width: '240px', flexShrink: 0 }}>
          <div className="card" style={{ padding: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', marginBottom: '16px', textTransform: 'uppercase' }}>
              Admin Panel
            </div>
            {[
              { id: 'dashboard', icon: '📊', label: 'Dashboard' },
              { id: 'users', icon: '👥', label: 'Users' },
              { id: 'broadcast', icon: '📢', label: 'Broadcast' },
              { id: 'settings', icon: '⚙️', label: 'Settings' },
              { id: 'audit', icon: '📋', label: 'Audit Log' },
            ].map(tab => (
              <button
                key={tab.id}
                className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-ghost'}`}
                style={{ width: '100%', justifyContent: 'flex-start', marginBottom: '8px' }}
                onClick={() => setActiveTab(tab.id)}
              >
                <span style={{ marginRight: '8px' }}>{tab.icon}</span> {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'users' && renderUsers()}
          {activeTab === 'user-detail' && renderUserDetail()}
          {activeTab === 'settings' && renderSettings()}
          {activeTab === 'broadcast' && renderBroadcast()}
          {activeTab === 'audit' && renderAuditLog()}
        </div>
      </div>
    </div>
  );
};

export default Admin;

